// The Sales home: the revenue forecast and what it is made of, the open
// pipeline by stage, and leads. Team-wide; fetched here, shaped by pure
// functions so they test.
import { ObjectsApiError } from '../objects/objectsApi';
import { addMonths } from '../delivery/model';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

// ── Time frame ─────────────────────────────────────────────────────────────

export type RangeKey = 'next3' | 'next6' | 'next12' | 'thisyear' | 'nextyear';

export const RANGES: readonly [RangeKey, string][] = [
  ['next3', 'Next 3 months'], ['next6', 'Next 6 months'], ['next12', 'Next 12 months'], ['thisyear', 'This year'], ['nextyear', 'Next year'],
];

export const rangeLabel = (key: RangeKey): string => RANGES.find(([k]) => k === key)?.[1] ?? '';

/** The months a time frame covers, from `today` (YYYY-MM-DD): its first month and how many. */
export function rangeWindow(key: RangeKey, today: string): { from: string; months: number } {
  const month = today.slice(0, 7);
  const year = Number(today.slice(0, 4));
  if (key === 'next3') return { from: month, months: 3 };
  if (key === 'next12') return { from: month, months: 12 };
  if (key === 'thisyear') return { from: `${year}-01`, months: 12 };
  if (key === 'nextyear') return { from: `${year + 1}-01`, months: 12 };
  return { from: month, months: 6 };
}

const windowQuery = (w: { from: string; months: number }) => `from=${w.from}&months=${w.months}`;

// ── Forecast ───────────────────────────────────────────────────────────────

/** One month as the server sends it. */
export interface ForecastMonth {
  /** YYYY-MM. */
  month: string;
  won: number;
  open_weighted: number;
  open_total: number;
  target: number | null;
}

/** One opportunity's revenue in one month. */
export interface OpportunityMonth {
  opportunity_id: string;
  opportunity_name: string;
  account_id: string;
  account_name: string;
  stage: string;
  probability: number | null;
  won: boolean;
  /** YYYY-MM. */
  month: string;
  amount: number;
  weighted: number;
}

export async function fetchForecast(fetcher: Fetch, w: { from: string; months: number }): Promise<ForecastMonth[]> {
  const response = await fetcher(`/api/revenue/forecast?${windowQuery(w)}`);
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load the revenue forecast.');
  return ((await response.json()) as { months?: ForecastMonth[] }).months ?? [];
}

export async function fetchForecastRows(fetcher: Fetch, w: { from: string; months: number }): Promise<OpportunityMonth[]> {
  const response = await fetcher(`/api/revenue/by-opportunity?part=all&${windowQuery(w)}`);
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load the revenue.');
  return ((await response.json()) as { rows?: OpportunityMonth[] }).rows ?? [];
}

/** A slice of the chart: a month, or every month, and a part of it. */
export interface Selection {
  month: string | null;
  part: 'all' | 'won' | 'open';
}

export const NO_SELECTION: Selection = { month: null, part: 'all' };

/** Clicking what is already selected clears it. */
export function toggleSelection(current: Selection, month: string, part: Selection['part']): Selection {
  return current.month === month && current.part === part ? NO_SELECTION : { month, part };
}

export function selectionTitle(sel: Selection, range: RangeKey): string {
  const when = sel.month ? monthLabel(sel.month) : rangeLabel(range);
  return sel.part === 'all' ? when : `${when} · ${sel.part}`;
}

/** A table row: an opportunity in the selected month, or across the range with its months counted. */
export interface ForecastRow {
  key: string;
  opportunityId: string;
  opportunity: string;
  accountId: string;
  account: string;
  stage: string;
  probability: number | null;
  won: boolean;
  months: number;
  amount: number;
  weighted: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function forecastRows(rows: OpportunityMonth[], sel: Selection): ForecastRow[] {
  const byOpp = new Map<string, ForecastRow & { seen: Set<string> }>();
  for (const r of rows) {
    if (sel.month && r.month !== sel.month) continue;
    if (sel.part === 'won' && !r.won) continue;
    if (sel.part === 'open' && r.won) continue;
    const row = byOpp.get(r.opportunity_id) ?? {
      key: r.opportunity_id, opportunityId: r.opportunity_id, opportunity: r.opportunity_name, accountId: r.account_id, account: r.account_name,
      stage: r.stage, probability: r.probability, won: r.won, months: 0, amount: 0, weighted: 0, seen: new Set<string>(),
    };
    row.seen.add(r.month);
    row.months = row.seen.size;
    row.amount = round2(row.amount + r.amount);
    row.weighted = round2(row.weighted + r.weighted);
    byOpp.set(r.opportunity_id, row);
  }
  return [...byOpp.values()].map(({ seen: _, ...row }) => row).sort((a, b) => b.amount - a.amount || a.opportunity.localeCompare(b.opportunity));
}

export const sumOf = <T>(rows: T[], pick: (r: T) => number): number => round2(rows.reduce((t, r) => t + pick(r), 0));

/** A whole percent of a target, or null without one. */
export const ofTarget = (total: number, target: number | null): number | null => (target ? Math.round((total / target) * 100) : null);

// ── Pipeline ───────────────────────────────────────────────────────────────

export interface OpenOpportunity {
  id: string;
  name: string;
  account_id: string | null;
  account_name: string | null;
  stage: string;
  amount: number | null;
  probability: number | null;
  close_date: string | null;
  /** Days from today to the close date; negative once it has passed. */
  close_days: number | null;
  has_quote: boolean;
}

export interface Pipeline {
  today: string;
  stages: string[];
  opportunities: OpenOpportunity[];
}

export async function fetchPipeline(fetcher: Fetch): Promise<Pipeline> {
  const response = await fetcher('/api/sales/pipeline');
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load the pipeline.');
  return (await response.json()) as Pipeline;
}

/** "Qualify", "Closed won". */
export const stageLabel = (stage: string): string => stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' ');

export const weightedOf = (o: OpenOpportunity): number => round2(((o.amount ?? 0) * (o.probability ?? 0)) / 100);

export interface StageRollup {
  key: string;
  label: string;
  deals: OpenOpportunity[];
  amount: number;
  weighted: number;
}

/** Every open stage in order, with its deals, open amount and weighted amount; then any stage the list didn't name. */
export function stageRollup(p: Pipeline): StageRollup[] {
  const keys = [...p.stages, ...p.opportunities.map((o) => o.stage).filter((s) => !p.stages.includes(s))];
  return [...new Set(keys)].map((key) => {
    const deals = p.opportunities.filter((o) => o.stage === key);
    return { key, label: stageLabel(key), deals, amount: sumOf(deals, (o) => o.amount ?? 0), weighted: sumOf(deals, weightedOf) };
  });
}

export interface AttentionDeal {
  deal: OpenOpportunity;
  due: { label: string; tone: 'overdue' | 'soon' | 'later' };
}

const dayCount = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** Deals closing within 30 days or past due, and deals with no quote yet, by close date. */
export function attentionDeals(p: Pipeline): AttentionDeal[] {
  return p.opportunities
    .filter((o) => (o.close_days !== null && o.close_days <= 30) || !o.has_quote)
    .map((deal) => {
      const d = deal.close_days;
      let due: AttentionDeal['due'];
      if (d !== null && d < 0) due = { label: `${dayCount(-d)} past due`, tone: 'overdue' };
      else if (d === 0) due = { label: 'Closes today', tone: 'soon' };
      else if (d !== null && d <= 30) due = { label: `Closes in ${dayCount(d)}`, tone: d <= 14 ? 'soon' : 'later' };
      else due = { label: 'Needs a quote', tone: 'later' };
      return { deal, due };
    });
}

// ── Leads ──────────────────────────────────────────────────────────────────

export interface Lead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_name: string | null;
  status: string;
  source: string | null;
  maturity_tier: string | null;
  urgency_score: string | null;
  /** YYYY-MM-DD. */
  created_on: string;
  /** Days since it came in. */
  days: number;
}

export async function fetchLeads(fetcher: Fetch): Promise<{ today: string; leads: Lead[] }> {
  const response = await fetcher('/api/sales/leads');
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load the leads.');
  return (await response.json()) as { today: string; leads: Lead[] };
}

const CLOSED_LEAD = new Set(['converted', 'disqualified']);
const AI_SOURCE = 'tywin';

export const isOpenLead = (l: Lead): boolean => !CLOSED_LEAD.has(l.status);

/** "AI assessment" for the website's assessment, otherwise the source as a label. */
export const sourceLabel = (source: string | null): string =>
  source === AI_SOURCE ? 'AI assessment' : source ? stageLabel(source) : '—';

export const leadName = (l: Lead): string =>
  l.company_name || `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() || l.email || 'Lead';

/** The last `count` months to this one, each with its leads converted and not. */
export function leadsByMonth(leads: Lead[], today: string, count = 8): { month: string; converted: number; other: number; leads: Lead[] }[] {
  const last = today.slice(0, 7);
  return Array.from({ length: count }, (_, i) => addMonths(last, i - count + 1)).map((month) => {
    const inMonth = leads.filter((l) => l.created_on.slice(0, 7) === month);
    const converted = inMonth.filter((l) => l.status === 'converted').length;
    return { month, converted, other: inMonth.length - converted, leads: inMonth };
  });
}

export function conversion(leads: Lead[]): { leads: number; converted: number; disqualified: number; ai: number; rate: number | null } {
  const converted = leads.filter((l) => l.status === 'converted').length;
  const disqualified = leads.filter((l) => l.status === 'disqualified').length;
  const decided = converted + disqualified;
  return {
    leads: leads.length, converted, disqualified,
    ai: leads.filter((l) => l.source === AI_SOURCE).length,
    rate: decided ? Math.round((converted / decided) * 100) : null,
  };
}

// ── Formatting ─────────────────────────────────────────────────────────────

const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "April 2027". */
export const monthLabel = (month: string): string => `${FULL_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

/** "Oct 31, 2026". */
export const dateLabel = (iso: string): string => `${SHORT_MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}`;

/** "$90,000". */
export const money = (n: number): string => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Today in this browser, YYYY-MM-DD, until the server says otherwise. */
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
